import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import api from '@/lib/api';
import { formatDateTime } from '@/utils/date';

interface Plan {
  id: string;
  appId: string;
  name: string;
  description: string | null;
  type: string;
  interval: string;
  price: number;
  features: string[] | null;
  priority: number;
  status: string;
  dailyCalls: number | null;
  throttleExceeded: boolean;
  application?: {
    id: string;
    name: string;
    appKey: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Application {
  id: string;
  appKey: string;
  name: string;
}

const TYPE_LABELS: Record<string, string> = {
  free: '免费版',
  monthly: '月度会员',
  yearly: '年度会员',
};

const INTERVAL_LABELS: Record<string, string> = {
  monthly: '每月',
  yearly: '每年',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<string, string> = {
  active: '活跃',
  inactive: '未激活',
};

export default function PlansList() {
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlan, setNewPlan] = useState({
    name: '',
    description: '',
    type: 'monthly',
    interval: 'monthly',
    price: 0,
    dailyCalls: null as number | null,
    throttleExceeded: false,
    features: [] as string[],
  });
  const queryClient = useQueryClient();

  // 获取应用列表
  const { data: appsData } = useQuery({
    queryKey: ['applications'],
    queryFn: async () => {
      return api.get('/admin/applications');
    },
    select: (response): Application[] => {
      // The response structure from backend is:
      // { status: 'success', code: 200, data: { data: Application[], total: number } }
      // OR with pagination metadata: { status: 'success', code: 200, data: Application[] }

      try {
        const responseData = (response as any)?.data?.data;

        if (!responseData) {
          return [];
        }

        // Check if it's a nested structure (with data.data and total)
        if (Array.isArray((responseData as any).data)) {
          return (responseData as any).data as Application[];
        }

        // Check if data itself is an array (pagination metadata extracted)
        if (Array.isArray(responseData)) {
          return responseData as Application[];
        }

        return [];
      } catch (error) {
        console.error('Failed to transform applications response:', error);
        return [];
      }
    },
    placeholderData: [],
  });

  // 获取套餐列表
  const {
    data: plans,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['plans', selectedAppId],
    queryFn: async () => {
      const params = selectedAppId ? `?appId=${selectedAppId}` : '';
      const response = await api.get<{ status: string; data: Plan[] }>(`/admin/plans${params}`);
      return response.data.data;
    },
    staleTime: 0,
    refetchOnMount: true,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newPlan & { appId: string }) => {
      const response = await api.post('/admin/plans', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      setShowCreateModal(false);
      setNewPlan({
        name: '',
        description: '',
        type: 'monthly',
        interval: 'monthly',
        price: 0,
        dailyCalls: null,
        throttleExceeded: false,
        features: [],
      });
    },
    onError: (error: any) => {
      alert(`创建失败: ${error.response?.data?.message || error.message}`);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const response = await api.put(`/admin/plans/${id}`, { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || '操作失败';
      alert(message);
    },
  });

  const getStatusBadge = (status: string) => {
    const colors = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
    const label = STATUS_LABELS[status] || status;
    return <span className={`px-2 py-1 text-xs font-medium rounded ${colors}`}>{label}</span>;
  };

  const handleCreate = () => {
    if (!selectedAppId) {
      alert('请先选择一个应用');
      return;
    }
    if (!newPlan.name) {
      alert('请填写套餐名称');
      return;
    }
    createMutation.mutate({
      ...newPlan,
      appId: selectedAppId,
    });
  };

  const handleToggleStatus = (id: string, name: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? '激活' : '停用';

    if (confirm(`确定要${action}套餐 "${name}" 吗？`)) {
      updateStatusMutation.mutate({ id, status: newStatus });
    }
  };

  const formatPrice = (price: number) => {
    return (price / 100).toFixed(2);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">套餐管理</h1>
          <p className="mt-1 text-sm text-gray-600">管理应用的订阅套餐和定价</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => refetch()}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            刷新
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={!selectedAppId}
            className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            创建套餐
          </button>
        </div>
      </div>

      {/* 应用选择器 */}
      <div className="bg-white rounded-lg shadow p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">选择应用</label>
        <select
          value={selectedAppId}
          onChange={(e) => setSelectedAppId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">全部应用</option>
          {appsData?.map((app) => (
            <option key={app.id} value={app.id}>
              {app.name} ({app.appKey})
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  套餐名称
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  所属应用
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  类型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  价格
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  每日限额
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  状态
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  优先级
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  创建时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    加载中...
                  </td>
                </tr>
              ) : !plans || plans.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500">
                    {selectedAppId ? '暂无套餐' : '请选择一个应用查看套餐'}
                  </td>
                </tr>
              ) : (
                plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{plan.name}</div>
                      {plan.description && (
                        <div className="text-xs text-gray-500">{plan.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {plan.application ? (
                        <div>
                          <div className="font-medium text-gray-900">{plan.application.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{plan.application.appKey}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">未分配</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div>{TYPE_LABELS[plan.type] || plan.type}</div>
                      <div className="text-xs text-gray-500">
                        {INTERVAL_LABELS[plan.interval] || plan.interval}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ¥{formatPrice(plan.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {plan.dailyCalls ? `${plan.dailyCalls} 次/天` : '无限制'}
                      {plan.throttleExceeded && (
                        <div className="text-xs text-yellow-600">超限后降速</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(plan.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {plan.priority}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDateTime(plan.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleToggleStatus(plan.id, plan.name, plan.status)}
                        className={`font-medium ${
                          plan.status === 'active'
                            ? 'text-yellow-600 hover:text-yellow-800'
                            : 'text-green-600 hover:text-green-800'
                        }`}
                      >
                        {plan.status === 'active' ? '停用' : '激活'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 创建套餐模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">创建新套餐</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">套餐名称 *</label>
                <input
                  type="text"
                  value={newPlan.name}
                  onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="专业版"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                <textarea
                  value={newPlan.description}
                  onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="套餐描述（可选）"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">类型</label>
                  <select
                    value={newPlan.type}
                    onChange={(e) => setNewPlan({ ...newPlan, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="free">免费版</option>
                    <option value="monthly">月度会员</option>
                    <option value="yearly">年度会员</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">周期</label>
                  <select
                    value={newPlan.interval}
                    onChange={(e) => setNewPlan({ ...newPlan, interval: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">每月</option>
                    <option value="yearly">每年</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">价格（元）</label>
                <input
                  type="number"
                  value={newPlan.price}
                  onChange={(e) => setNewPlan({ ...newPlan, price: Number(e.target.value) * 100 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  每日调用次数限制
                </label>
                <input
                  type="number"
                  value={newPlan.dailyCalls || ''}
                  onChange={(e) =>
                    setNewPlan({
                      ...newPlan,
                      dailyCalls: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="留空表示不限制"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="throttleExceeded"
                  checked={newPlan.throttleExceeded}
                  onChange={(e) => setNewPlan({ ...newPlan, throttleExceeded: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="throttleExceeded" className="ml-2 block text-sm text-gray-900">
                  超限后降速（而非禁止）
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {createMutation.isPending ? '创建中...' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
